import styles from './VerifyEmailAddress.module.scss';

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import { useTranslation } from 'react-i18next';

import { Icon } from 'components/Icon';
import { Modal } from 'components/Modal';
import { Button, ButtonVariant } from '@clover/web-lib-consumer-ui';
import { CloseButton } from 'components/CloseButton';
import { FieldError } from 'components/FieldError';

import { useCustomer } from 'hooks/context/customer/customer';
import { useCloseNavigate } from 'hooks/useCloseNavigate/useCloseNavigate';
import { useReCaptcha } from 'hooks/context/ReCaptcha';
import { useSmartNavigate } from 'hooks/useSmartNavigate';

import { parseAuthErrorMessage } from 'util/ErrorHandling/errorHandling.util';
import { onClosePath } from '../Authentication.util';
import { generateId } from 'util/generateId';
import { RouteFC } from 'util/routeTypes';

import { VERIFY_EMAIL_ADDRESS } from './const';

export enum VerifyEmailErrors {
  EMAIL_MAX_TOKENS_EXCEEDED = 'EMAIL_MAX_TOKENS_EXCEEDED',
  EMAIL_CONFIRMATION_TOKEN_INVALID = 'EMAIL_CONFIRMATION_TOKEN_INVALID',
}

type VerifyEmailAddressLocationStateProps = {
  emailAddress: string;
  redirectPath: string;
}

export const VerifyEmailAddress: RouteFC<{ children: never, props: RouteComponentProps }> = memo((props) => {
  const state = props.location?.state;
  const { redirectPath, emailAddress } = state as VerifyEmailAddressLocationStateProps || {};

  // Hooks
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const { sendEmailAuth } = useCustomer();
  const { attemptReCaptcha } = useReCaptcha();
  const onClose = useCloseNavigate(onClosePath(redirectPath));
  const errorTextId = useRef(generateId()).current;

  // States
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorState, setShowErrorState] = useState(false);

  // Callbacks
  const sendCode = useCallback(async () => {
    const reCaptchaToken = await attemptReCaptcha();
    try {
      await sendEmailAuth(emailAddress, redirectPath, reCaptchaToken);
    } catch (error) {
      setErrorMessage(parseAuthErrorMessage(error));
      setShowErrorState(true);
    }
  }, [attemptReCaptcha, emailAddress, redirectPath, sendEmailAuth]);

  // If customer is linked here without a emailAddress in state return to login
  useEffect(() => {
    if (!emailAddress) navigate('.info', { state: { redirectPath } });
  }, [navigate, emailAddress, redirectPath]);

  return (
    <Modal
      aria={{ labelledby: VERIFY_EMAIL_ADDRESS.id }}
      className={styles.modal}
      isOpen={true}
      onClose={onClose}
      testId={VERIFY_EMAIL_ADDRESS.root}
    >
      <header className={styles.modalHeader}>
        <Icon className={styles.leftArrowIcon} name="leftArrow" size={18} onClick={() => navigate('.info', { state: { redirectPath } })} data-testid={VERIFY_EMAIL_ADDRESS.backIcon}></Icon>
        {onClose && <CloseButton className={styles.closeButton} onClick={onClose} />}
      </header>

      <section className={styles.body}>
        <Icon className={styles.icon} name={'email'} height={48} width={60}></Icon>
        <p className={styles.title} id={VERIFY_EMAIL_ADDRESS.id}>{t('emailValidation.title')}</p>
        <div className={styles.subtitle}>
          <div>{t('emailValidation.subtitle')}</div>
          <div className={styles.email}>{ emailAddress }</div>
        </div>
        {showErrorState && (
          <FieldError id={errorTextId} data-testid={errorMessage} className={styles.errorText}>{t(`authErrorMessage.${errorMessage}`, { contact: emailAddress })}</FieldError>
        )}
      </section>
      {showErrorState && <footer className={styles.footer}>
        <Button className={styles.button} onClick={() => navigate('.info', { state: { redirectPath } })} data-testid={VERIFY_EMAIL_ADDRESS.loginWithPhone}>{t('authForm.loginAsPhone')}</Button>
        <Button variant={ButtonVariant.INVERTED} className={styles.button} onClick={() => navigate(redirectPath)} data-testid={VERIFY_EMAIL_ADDRESS.checkout}>{t('authForm.checkoutAsGuest')}</Button>
      </footer>}
      {!showErrorState && <footer className={styles.footer}>
        <Button variant={ButtonVariant.INVERTED} className={styles.button} onClick={sendCode} data-testid={VERIFY_EMAIL_ADDRESS.resend}>{t('emailValidation.resendCode')}</Button>
      </footer>}
    </Modal>
  );
});
